<script lang="ts">
    import emblaCarouselSvelte from "embla-carousel-svelte";
    import { createEventDispatcher, onDestroy, onMount } from "svelte";

    import type { EmblaSliderApi } from "./types";

    // Constants
    const MIN_EMBLA_DURATION_MS = 0; // Minimum duration for transitions (0 allows instant transitions)

    // Public props
    export let className = "";
    export { className as class };
    export let perPage = 5; // how many items to show at once
    export let loop = false;
    export let duration = 40; // animation duration in ms (Embla's `duration` option)
    export let autoHeight = false; // parity flag with the legacy slider
    export let controls = true; // show arrows like legacy Slider
    export let gap = 16; // gap between slides in pixels
    export let draggable = true; // enable/disable drag functionality
    export let setApi: ((api: EmblaSliderApi | null) => void) | undefined =
        undefined;

    // Slot props (exposed to consumer)
    let hasPrev = false;
    let hasNext = false;

    let embla: any | null = null;

    const dispatch = createEventDispatcher();

    function updateNavState() {
        if (!embla) return;
        hasPrev = embla.canScrollPrev();
        hasNext = embla.canScrollNext();
    }

    // Options for the svelte action; keep them reactive with our props
    $: options = {
        loop,
        duration: Math.max(MIN_EMBLA_DURATION_MS, duration),
        dragFree: false,
        watchDrag: draggable,
        align: "start" as const,
        slidesToScroll: perPage,
    };

    $: actionParams = { options, plugins: [] };

    function subscribeToEmblaEvents() {
        if (!embla) return;

        embla.on("reInit", updateNavState);
        embla.on("select", () => {
            updateNavState();
            dispatch("select");
        });
        embla.on("slidesChanged", () => {
            updateNavState();
            dispatch("slidesChanged");
        });
    }

    function handleEmblaInit(event: CustomEvent<any>) {
        embla = event.detail;
        if (!embla) return;

        subscribeToEmblaEvents();
        updateNavState();
        dispatch("ready");
    }

    // Internal methods for API object
    function scrollNext(): void {
        embla?.scrollNext();
    }
    function scrollPrev(): void {
        embla?.scrollPrev();
    }
    function scrollTo(index: number): void {
        embla?.scrollTo(index);
    }
    function canScrollNext(): boolean {
        return embla?.canScrollNext() || false;
    }
    function canScrollPrev(): boolean {
        return embla?.canScrollPrev() || false;
    }
    function selectedScrollSnap(): number {
        return embla?.selectedScrollSnap() || 0;
    }
    function reInit(): void {
        if (!embla) return;
        try {
            // Preserve current index during manual reInit
            const currentIndex = embla.selectedScrollSnap();
            embla.reInit(options);
            embla.scrollTo(currentIndex, true);
            updateNavState();
        } catch (e) {
            console.error("Error reinitializing Embla:", e);
        }
    }

    $: {
        options;
        reInit();
    }

    onDestroy(() => {
        // clear API on destroy to avoid stale references
        setApi?.(null);
        embla?.destroy();
    });

    // Expose API to consumer once the component is mounted
    onMount(() => {
        setApi?.({
            scrollNext,
            scrollPrev,
            scrollTo,
            canScrollNext,
            canScrollPrev,
            selectedScrollSnap,
            reInit,
        });
    });
</script>

<div
    class="gw-slider {className}"
    style={`--per-page: ${perPage}; --gap: ${gap}px;`}
>
    <div
        class="gw-slider__slides"
        class:gw-slider__slides--autoHeight={autoHeight}
        use:emblaCarouselSvelte={actionParams}
        on:emblaInit={handleEmblaInit}
    >
        <div class="gw-slider__track">
            <slot name="slides" />
        </div>

        {#if controls}
            <slot name="arrows" {hasPrev} {hasNext} />
        {/if}
    </div>
    <slot name="dots" />
</div>

<style>
    .gw-slider {
        width: 100%;
    }
    .gw-slider__slides {
        position: relative;
        width: 100%;
        overflow: hidden;
    }
    .gw-slider__track {
        display: flex;
        gap: var(--gap, 16px);
    }

    .gw-slider__track > :global(*) {
        flex: 0 0
            calc(
                (100% - (var(--gap, 16px) * (var(--per-page) - 1))) /
                    var(--per-page)
            );
    }

    .gw-slider__slides--autoHeight {
        height: 0;
        transition: height 0.2s ease-in-out;
    }
</style>
